import React from 'react';
export function TransformationTimeline() {
  return <section className="space-y-8 text-lg leading-relaxed text-stone-800 my-12" data-id="element-254">
      <div className="space-y-4" data-id="element-255">
        <h3 className="text-xl sm:text-2xl font-bold text-stone-900 font-serif leading-snug" data-id="element-256">
          5 - Avedis, for the patients who have already been using this in your
          office — what kind of results are they actually reporting, and how
          quickly?
        </h3>
        <img src="https://cdn.magicpatterns.com/uploads/khQruF4Y9Hr3R9mfBiUkW9/hf_20260411_024246_27180787-b2e8-447f-bdce-969989d18c2e_(1).png" alt="Happy patient smiling in dentist chair after using ClearGuard Pro" className="w-full rounded-md" data-id="element-257" />
        <p data-id="element-258">
          <strong data-id="element-259">Avedis:</strong> Right as the very first chew dissolves in
          your mouth, you're going to immediately feel cleaner…
        </p>
        <p data-id="element-260">
          That's because you're flooding your mouth with 4 billion units of
          healthy bacteria - not to mention the very mineral your teeth have
          been missing.
        </p>
        <p className="font-[700]" data-id="element-261">
          But there's something you must be aware of - and it's that the real
          changes don't happen overnight.
        </p>
        <p data-id="element-262">
          In fact, most people don't know their oral microbiome is highly
          unique, just like their fingerprint.
        </p>
        <p data-id="element-263">
          And since everyone's biology is different, some notice changes within
          the first few days, while it may take longer for others.
        </p>
        <p data-id="element-264">
          Nonetheless, here's the general pattern I've noticed after sharing
          this with countless patients over the past few years, along with what
          the studies show.
        </p>
      </div>

      {/* Week 1-2 */}
      <div className="space-y-4" data-id="element-265">
        <h4 className="sm:text-xl font-bold text-[#2D6A4F] font-serif text-[24px]" data-id="element-266">
          Week 1–2:
        </h4>
        <p className="italic" data-id="element-267">
          The hydroxyapatite begins to bond to the enamel surface, filling
          micro-cracks and smoothing the surface that bacteria have been
          gripping onto for years.
        </p>
        <p className="font-[700]" data-id="element-268">
          The first thing almost everyone mentions is that their mouth just
          feels... cleaner.
        </p>
        <p data-id="element-269">Not just right after brushing, but throughout the entire day.</p>
        <p data-id="element-270">
          That "just left the dentist" feeling that normally disappears within
          an hour? It starts sticking around.
        </p>
        <p data-id="element-271">
          Morning breath is usually the second thing people notice, and their
          partners usually notice it before they even say anything.
        </p>
        <p data-id="element-272">
          For people with sensitivity, cold water starts feeling less like a
          jolt and more like just... cold water.
        </p>
        <p data-id="element-273">
          And gums that were tender every single morning? They start feeling
          less angry during every brushing.
        </p>
      </div>

      {/* Week 3-4 */}
      <div className="space-y-4" data-id="element-274">
        <h4 className="sm:text-xl font-bold text-[#2D6A4F] font-serif text-[24px]" data-id="element-275">
          Week 3–4:
        </h4>
        <p className="italic" data-id="element-276">
          The probiotic strains are establishing dominance — crowding out the
          bacteria responsible for bleeding and bad breath while the once
          impenetrable bacterial fortress begins to lose its grip.
        </p>
        <p className="font-[700]" data-id="element-277">
          This is where it gets interesting, because people start texting me
          around the third or fourth week.
        </p>
        <p data-id="element-278">
          The bleeding when they brush starts to slow down and come to a full
          stop.
        </p>
        <p data-id="element-279">
          Teeth start feeling noticeably smoother to the tongue - as that rough,
          almost gritty feeling starts disappearing.
        </p>
        <p data-id="element-280">
          And during this time, something else happens that I find really
          meaningful…
        </p>
        <p className="font-[700]" data-id="element-281">
          People stop thinking about their breath before they speak to someone.
        </p>
        <p data-id="element-282">
          That mental check they've been doing for years just... starts fading.
        </p>
        <p data-id="element-283">
          And this makes sense, because the very acid-producing bacteria that
          causes bad breath in the first place finally loses its grip.
        </p>
      </div>

      {/* Week 6-8 */}
      <div className="space-y-4" data-id="element-284">
        <h4 className="sm:text-xl font-bold text-[#2D6A4F] font-serif text-[24px]" data-id="element-285">
          Week 6–8:
        </h4>
        <p className="italic" data-id="element-286">
          The mouth's biological guard is now reestablished. Enamel is
          measurably smoother, and the chronic bacterial infection driving gum
          inflammation is losing ground at a clinical level.
        </p>
        <p className="font-[700]" data-id="element-287">
          This is the window where even dentists start asking questions.
        </p>
        <p data-id="element-288">
          Patients come in for their regular checkup and their hygienist notices
          something is different before they even mention anything…
        </p>
        <p data-id="element-289">
          Gum pockets are shallower. Less bleeding on probing. Sometimes no new
          cavities where there were always new cavities before.
        </p>
        <p data-id="element-290">
          Not to mention, this is when the smiling starts - and I notice it in
          my own waiting room.
        </p>
        <p data-id="element-291">
          People who used to sit with their lips pressed together start carrying
          themselves differently.
        </p>
        <p data-id="element-292">
          Their faces are more relaxed. They speak freely. They stand taller.
        </p>
      </div>

      {/* Month 3-5+ */}
      <div className="space-y-4" data-id="element-293">
        <h4 className="sm:text-xl font-bold text-[#2D6A4F] font-serif text-[24px]" data-id="element-294">
          Month 3–5+:
        </h4>
        <p className="italic" data-id="element-295">
          As every chew has been building on the last - the oral environment has
          fundamentally shifted from a state of chronic vulnerability to
          biological resilience.
        </p>
        <p className="font-[700]" data-id="element-296">
          This is where the full picture starts to come together.
        </p>
        <p data-id="element-297">
          I've had patients come back telling me they saved hundreds of dollars
          because their deep cleaning got canceled.
        </p>
        <p data-id="element-298">
          Dentists who were tracking their progression were surprised to find
          gums that look healthier and less inflamed – without any further
          recession.
        </p>
        <p data-id="element-299">
          But the thing that gets me every single time? And I've heard this more
          than once now.
        </p>
        <p className="font-[700]" data-id="element-300">
          Patients tell me their spouse or their kids notice something different
          about the way they smile before they ever said a word about what they
          changed.
        </p>
        <p data-id="element-301">That's when I know the two-step protocol is really working.</p>
      </div>
    </section>;
}